import * as React from 'react';

import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '@/modules/common/lib/utils';

const inputVariants = cva(
  'text-base w-full flex inline-flex gap-1 items-center justify-center whitespace-nowrap rounded-md font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-block disabled:opacity-50 focus-visible:sticky focus-visible:z-20 focus-visible:top-0 focus:bottom-0',
  {
    variants: {
      variant: {
        ghost:
          'border-none bg-transparent disabled:bg-gray-200 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500',
        filled:
          'font-normal flex w-full items-center justify-between gap-2 rounded-xl bg-gray-100 px-3 py-1',
        outline:
          'font-normal flex w-full items-center justify-between gap-2 rounded-xl px-3 py-1 border',
      },
      sizeType: {
        l: 'h-12 py-3 px-3 rounded-[12px]',
        m: 'h-8 py-[6px] rounded-[8px] px-3',
      },
      focused: {
        true: '',
        false: '',
      },
      hasError: {
        true: 'border border-red-500',
        false: '',
      },
      hasIcon: {
        true: 'pl-10',
        false: '',
      },
      hasButton: {
        true: 'pr-10',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      sizeType: 'l',
      focused: false,
      hasError: false,
      hasIcon: false,
      hasButton: false,
    },
  }
);

const labelVariants = cva(
  'pointer-events-none absolute z-1 transform text-gray-500 transition-all duration-300',
  {
    variants: {
      position: {
        top: '-top-1 translate-y-0 text-xs',
        center: 'top-1/2 -translate-y-1/2',
      },
      hasIcon: {
        true: 'left-10',
        false: 'left-1',
      },
      isFocused: {
        true: 'z-30',
        false: '',
      },
    },
    defaultVariants: {
      position: 'center',
      hasIcon: false,
    },
  }
);

const iconVariants = cva('absolute top-1/2 z-1 -translate-y-1/2 transform', {
  variants: {
    sizeType: {
      l: 'left-3',
      m: 'left-2',
    },
  },
  defaultVariants: {
    sizeType: 'l',
  },
});

const horizontalLineVariants = cva('absolute bottom-0 z-1 h-[1px] w-full', {
  variants: {
    color: {
      black: 'bg-black',
      red: 'bg-red-500',
      gray: 'bg-gray-200',
    },
  },
  defaultVariants: {
    color: 'gray',
  },
});

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  icon?: React.ReactNode;
  button?: React.ReactNode;
  errorMessage?: string;
  label?: string;
  hideErrorMessage?: boolean;
  setErrorMessage?: (value: string) => void;
  setValue?: (value: string) => void;
  validate?: (value: string) => boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      sizeType,
      button,
      variant,
      icon,
      errorMessage,
      label,
      hideErrorMessage,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [isFocused, setIsFocused] = React.useState(false);
    const [hasText, setHasText] = React.useState(!!inputRef?.current?.value || !!props.value);

    // Expose the internal ref to the parent component through the forwarded ref
    React.useImperativeHandle(ref, () => inputRef.current!);

    React.useEffect(() => {
      if (inputRef.current?.value || props.value) {
        setHasText(true);
      } else {
        setHasText(false);
      }
    }, [inputRef.current?.value, props.value]);

    return (
      <fieldset className="w-full" data-testid="input-fieldset">
        <label className="relative block w-full" data-testid="input-label">
          {label && (
            <span
              className={cn(
                labelVariants({
                  position: hasText || isFocused ? 'top' : 'center',
                  hasIcon: !!icon,
                  isFocused: isFocused,
                })
              )}
              data-testid="input-label-text"
            >
              {label}
            </span>
          )}
          {icon && (
            <span
              className={cn(iconVariants({ sizeType }), isFocused && 'z-30')}
              data-testid="input-icon"
            >
              {icon}
            </span>
          )}
          <input
            ref={inputRef}
            type={type}
            disabled={disabled}
            className={cn(
              inputVariants({
                sizeType,
                variant,
                focused: isFocused,
                hasError: !!errorMessage,
                hasIcon: !!icon,
                hasButton: !!button,
              }),
              className
            )}
            data-testid={`input-element-${props.name}`}
            data-value={inputRef.current?.value || props.value}
            {...props}
            onFocus={(e) => {
              if (props.onFocus) {
                props.onFocus(e);
              }
              setIsFocused(true);
            }}
            onBlur={(e) => {
              if (props.onBlur) {
                props.onBlur(e);
              }
              setIsFocused(false);
            }}
          />
          {(!variant || variant === 'ghost') && !disabled ?
            <div
              className={cn(
                horizontalLineVariants({
                  color:
                    isFocused ? 'black'
                    : errorMessage ? 'red'
                    : 'gray',
                }),
                isFocused && 'z-30'
              )}
              data-testid="input-horizontal-line"
            />
          : null}
          {button ?
            <span
              className={`absolute right-2 top-1/2 z-1 -translate-y-1/2 transform ${isFocused && 'z-30'}`}
              data-testid="input-button"
            >
              {button}
            </span>
          : null}
        </label>
        {!hideErrorMessage && !!errorMessage && (
          <div className="w-full pt-1" data-testid="input-description">
            {errorMessage && (
              <p className="text-sm font-medium text-destructive" data-testid="input-error-message">
                {errorMessage}
              </p>
            )}
          </div>
        )}
      </fieldset>
    );
  }
);

Input.displayName = 'Input';

export { Input };
